import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit2, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { toast } from 'sonner';
import { groupByMonth } from '@/utils/groupByMonth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CuttingProduction() {
  const [cutting, setCutting] = useState([]);
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [availableSections, setAvailableSections] = useState([]);
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canModify = (entry) => user.role === 'admin' || entry.created_by === user.username;
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    part_id: '',
    qty: '',
    source: 'RM',
    section: ''
  });

  const fetchCutting = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transactions/cutting`, {
        
      });
      setCutting(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to load cutting data');
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/masters/items`, {
        
      });
      setItems(response.data);
    } catch (err) { console.error("Failed to load dropdown data:", err); }
  }, []);

  useEffect(() => {
    fetchCutting();
    fetchItems();
  }, [fetchCutting, fetchItems]);

  const handlePartChange = (partId) => {
    const item = items.find(i => i.part_id === partId);
    if (item) {
      const sections = [item.section, ...(item.alternate_sections || [])];
      setAvailableSections(sections);
      setFormData({...formData, part_id: partId, section: item.section});
    } else {
      setAvailableSections([]);
      setFormData({...formData, part_id: partId, section: ''});
    }
  };

  const resetForm = () => {
    setFormData({ date: new Date().toISOString().split('T')[0], part_id: '', qty: '', source: 'RM', section: '' });
    setEditingId(null);
    setShowForm(false);
    setAvailableSections([]);
  };

  const handleEdit = (entry) => {
    const item = items.find(i => i.part_id === entry.part_id);
    const sections = item ? [item.section, ...(item.alternate_sections || [])] : [];
    setAvailableSections(sections);
    setFormData({
      date: new Date(entry.date).toISOString().split('T')[0],
      part_id: entry.part_id,
      qty: entry.qty.toString(),
      source: entry.source || 'RM',
      section: entry.section || (item ? item.section : '')
    });
    setEditingId(entry.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const data = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        qty: parseInt(formData.qty),
        section: formData.section || null,
        created_by: user.username
      };

      if (editingId) {
        await axios.put(`${API}/transactions/cutting/${editingId}`, data, {
          
        });
        toast.success('Cutting entry updated successfully');
      } else {
        await axios.post(`${API}/transactions/cutting`, data, {
          
        });
        toast.success('Cutting production created successfully');
      }
      resetForm();
      fetchCutting();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save cutting entry');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this cutting entry?')) return;
    try {
      await axios.delete(`${API}/transactions/cutting/${id}`, {
        
      });
      toast.success('Cutting entry deleted successfully');
      fetchCutting();
    } catch (error) {
      toast.error('Failed to delete cutting entry');
    }
  };

  // Get item info helper for table display
  const getItemSection = (entry) => {
    if (entry.section) return entry.section;
    const item = items.find(i => i.part_id === entry.part_id);
    return item ? item.section : '—';
  };

  const toggleMonth = (key) => setCollapsedMonths(prev => ({...prev, [key]: !prev[key]}));
  const monthGroups = groupByMonth(cutting);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="cutting-production-title">CUTTING PRODUCTION</h1>
          <p className="text-sm text-gray-700 mt-2">Record cutting operations</p>
        </div>
        <Button
          data-testid="toggle-cutting-form-button"
          onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
          className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
        >
          {showForm ? <><X className="w-4 h-4 mr-2" />CANCEL</> : <><Plus className="w-4 h-4 mr-2" />NEW ENTRY</>}
        </Button>
      </div>

      {showForm && (
        <div className="bg-white border border-border shadow-sm p-6">
          <h2 className="text-2xl font-bold uppercase mb-6" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
            {editingId ? 'EDIT CUTTING ENTRY' : 'NEW CUTTING ENTRY'}
          </h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-5 gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Date</Label>
              <Input
                data-testid="cutting-date-input"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Part ID</Label>
              <SearchableSelect
                testId="cutting-part-id-select"
                className="mt-1.5"
                options={items.map(item => ({ value: item.part_id, label: `${item.part_id} - ${item.part_name}`, searchStr: `${item.part_id} ${item.part_name}` }))}
                value={formData.part_id}
                onValueChange={handlePartChange}
                placeholder="Search part..."
                searchPlaceholder="Type to search parts..."
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Section</Label>
              <Select
                value={formData.section}
                onValueChange={(value) => setFormData({...formData, section: value})}
                disabled={availableSections.length === 0}
              >
                <SelectTrigger data-testid="cutting-section-select" className="mt-1.5 rounded-none">
                  <SelectValue placeholder={availableSections.length === 0 ? "Select part first" : "Select section"} />
                </SelectTrigger>
                <SelectContent>
                  {availableSections.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Quantity</Label>
              <Input
                data-testid="cutting-qty-input"
                type="number"
                value={formData.qty}
                onChange={(e) => setFormData({...formData, qty: e.target.value})}
                required
                className="mt-1.5 rounded-none"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Source</Label>
              <Select
                value={formData.source}
                onValueChange={(value) => setFormData({...formData, source: value})}
                required
              >
                <SelectTrigger data-testid="cutting-source-select" className="mt-1.5 rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RM">Raw Material</SelectItem>
                  <SelectItem value="Bought Cutting">Bought Cutting</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-5 flex justify-end pt-4">
              <Button
                data-testid="submit-cutting-button"
                type="submit"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                {editingId ? 'UPDATE ENTRY' : 'CREATE ENTRY'}
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-border shadow-sm">
        <div className="border-b border-border p-4 bg-gray-50/50">
          <h2 className="text-xl font-semibold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>RECENT ENTRIES</h2>
        </div>
        {cutting.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No cutting entries found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Date</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Section</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Quantity</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Source</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {monthGroups.map(group => (
                  <React.Fragment key={`group-${group.key}`}>
                    <tr className="bg-gray-100 cursor-pointer" onClick={() => toggleMonth(group.key)}>
                      <td colSpan={6} className="p-3">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                          {collapsedMonths[group.key] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          {group.label} ({group.entries.length} entries)
                        </div>
                      </td>
                    </tr>
                    {!collapsedMonths[group.key] && group.entries.map((entry, idx) => (
                  <tr key={entry.id || idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`cutting-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs">{new Date(entry.date).toLocaleDateString()}</td>
                    <td className="p-4 align-middle font-mono text-xs">{entry.part_id}</td>
                    <td className="p-4 align-middle text-xs">{getItemSection(entry)}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{entry.qty}</td>
                    <td className="p-4 align-middle">
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-slate-100 text-slate-700">
                        {entry.source}
                      </span>
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center justify-center gap-1">
                        {canModify(entry) && (<>
                        <button
                          data-testid={`edit-cutting-${idx}`}
                          onClick={() => handleEdit(entry)}
                          className="p-2 hover:bg-blue-50 hover:text-blue-600 transition-all rounded-sm"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-cutting-${idx}`}
                          onClick={() => handleDelete(entry.id)}
                          className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
